import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

// Styled components for the integrations section
const IntegrationsContainer = styled.div`
  background: #f8fafc;
  position: relative;
  overflow: hidden;
  height: 100vh;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 700px) {
    height: 535px !important;
    min-height: 400px;
  }


`;

const FloatingIcon = styled(motion.div)<{ top: string; left: string; delay: number }>`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  width: 80px;
  height: 80px;
  background: #ffffff;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  padding: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media screen and (max-width: 700px) {
    width: 60px;
    height: 60px;
    padding: 8px;
  }
`;

const ContentContainer = styled(motion.div)`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 3;
  position: relative;

  @media screen and (max-width: 700px) {
    padding: 0 1rem;
    max-width: 100%;
  }

  @media screen and (min-width: 701px) and (max-width: 1100px) {
    padding: 0 2rem;
    max-width: 700px;
  }
`;

const Title = styled(motion.h2)`
  font-size: 40px;
  line-height: 50px;
  margin-bottom: 20px;
  text-align: center;
  color: #1a202c;

  @media screen and (max-width: 700px) {
    font-size: 30px;
    line-height: 40px;
    margin-bottom: 15px;
  }

  @media screen and (min-width: 701px) and (max-width: 1100px) {
    font-size: 36px;
    line-height: 45px;
  }
`;

const Highlight = styled.span`
  background: linear-gradient(to right, #DD8FF9 0%, #A975FE 33%, #4F46E5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled(motion.p)`
  font-size: 16px;
  font-weight: 400 !important;
  text-align: center;
  color: #5B5B5B !important;
  line-height: 1.6;
  margin: 0;

  @media screen and (max-width: 700px) {
    font-size: 14px;
  }

  @media screen and (min-width: 701px) and (max-width: 1100px) {
    font-size: 15px;
  }
`;

// Integration icons data with actual brand logos
const integrationIcons = [
  { 
    logo: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/quickbooks.svg', 
    top: '15%', 
    left: '10%', 
    delay: 0.2, 
    title: 'QuickBooks' 
  },
  { 
    logo: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/googlecalendar.svg', 
    top: '25%', 
    left: '85%', 
    delay: 0.4, 
    title: 'Google Calendar' 
  },
  { 
    logo: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/microsoftoutlook.svg', 
    top: '70%', 
    left: '15%', 
    delay: 0.6, 
    title: 'Outlook' 
  },
  { 
    logo: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/instagram.svg', 
    top: '80%', 
    left: '80%', 
    delay: 0.8, 
    title: 'Instagram' 
  },
  { 
    logo: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/meta.svg', 
    top: '45%', 
    left: '5%', 
    delay: 1.0, 
    title: 'Meta' 
  },
  { 
    logo: 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/google.svg', 
    top: '35%', 
    left: '90%', 
    delay: 1.2, 
    title: 'Google' 
  },
];

const IntegrationsSection: React.FC = () => {
  return (
    <IntegrationsContainer>
      {/* Floating integration icons */}
      {integrationIcons.map((item, index) => (
        <FloatingIcon
          key={index}
          top={item.top}
          left={item.left}
          delay={item.delay}
          title={item.title}
          initial={{ opacity: 0, scale: 0, rotate: -180 }}
          whileInView={{ 
            opacity: 1, 
            scale: 1, 
            rotate: 0,
            y: [0, -10, 0]
          }}
          transition={{ 
            duration: 0.8, 
            delay: item.delay,
            y: {
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }
          }}
          viewport={{ once: true }}
          whileHover={{ 
            scale: 1.1,
            rotate: 5,
            transition: { duration: 0.2 }
          }}
        >
          <img 
            src={item.logo} 
            alt={item.title}
            style={{ filter: 'brightness(0) saturate(100%)' }} // Makes SVGs black by default
          />
        </FloatingIcon>
      ))}

      <ContentContainer
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <Title
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <Highlight>Seamless Integration </Highlight> All of your favorite tools
        </Title>
        <Description
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          Connect ServTrackr with your existing tools. No disruptions, no learning curves—just a smoother workflow from day one.
        </Description>
      </ContentContainer>
    </IntegrationsContainer>
  );
};

export default IntegrationsSection;
